import React from 'react';
import { Alert, Button, Modal, ModalProps } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Formik, useFormikContext } from 'formik';
import { object, string, date, number } from 'yup';
import { map } from 'lodash';
import { TrixEditor } from 'react-trix';
import { useSelector } from 'react-redux';

import { GenderFormGroupSelect } from '@components/Feature/User';
import { FormGroupControl, FormGroupSelect } from '@components/Base';
import { InsurancePolicy } from '@models/InsurancePolicy';
import { InsurancePolicyUserList } from '@models/InsurancePolicyUser';
import { noob } from '@utils/typescript';
import { mergeInitialValues } from '@utils/formik';
import { ServerError } from '@components/Helper';

import { selectFeatureInsurancePolicyUser } from './selector';
import { InsurancePolicyUserCreatePayload } from './action';

export type InsurancePolicyUserFormValues = Omit<InsurancePolicyUserCreatePayload, 'insurancePolicyId'>;

const GROUP_PROPS = { className: 'mb-3' };

type FormFormProps = Pick<ModalProps, 'onHide'> & Pick<InsurancePolicy, 'benefitTemplates'> & {
  isEdit: boolean;
};

const FormForm = ({ onHide, benefitTemplates, isEdit }: FormFormProps) => {
  const { t } = useTranslation();
  const { serverErrors } = useSelector(selectFeatureInsurancePolicyUser);
  const { values, setFieldValue } = useFormikContext<InsurancePolicyUserFormValues>();

  return (
    <Form>
      <Modal.Header closeButton>
        <Modal.Title>{t('common:member')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {serverErrors?.userId && (
          <Alert variant="danger">{serverErrors.userId}</Alert>
        )}

        <FormGroupSelect
          asRow
          name="benefitTemplateId"
          label="Coverage"
          groupProps={GROUP_PROPS}
          required
        >
          <option></option>
          {map(benefitTemplates, ({ id, name }) => (
            <option key={name} value={id}>
              {name}
            </option>
          ))}
        </FormGroupSelect>
        <FormGroupControl
          asRow
          name="insurerMemberId"
          label="Insurer Member ID"
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="name"
          label={t('user.name')}
          required
          disabled={isEdit}
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="dob"
          label={t('user.dob')}
          type="date"
          required
          disabled={isEdit}
          groupProps={GROUP_PROPS}
        />
        <GenderFormGroupSelect
          asRow
          name="gender"
          required
          disabled={isEdit}
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="occupation"
          label={t('user.occupation')}
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="email"
          label={t('user.email')}
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="effectiveDate"
          label={t('common:effectiveDate')}
          type="date"
          required
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="expirationDate"
          label={t('common:expirationDate')}
          type="date"
          required
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="premium"
          label={t('common:premium')}
          type="number"
          required
          groupProps={GROUP_PROPS}
        />
        <FormGroupControl
          asRow
          name="refundedPremium"
          label={t('common:refundedPremium')}
          type="number"
          groupProps={GROUP_PROPS}
        />

        <p>{t('common:fillDescriptionDetails')}</p>
        <TrixEditor
          className="trix-content"
          onChange={(html) => setFieldValue('descriptionHtml', html)}
          onEditorReady={noob}
          mergeTags={[]}
          value={values.descriptionHtml}
          placeholder={t('common:description')}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('confirmModal.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('common:save')}
        </Button>
      </Modal.Footer>
      <ServerError errors={serverErrors} />
    </Form >
  );
}

const defaultValues: InsurancePolicyUserFormValues = {
  benefitTemplateId: '',
  insurerMemberId: '',
  name: '',
  dob: '',
  gender: '',
  occupation: '',
  email: '',
  effectiveDate: '',
  expirationDate: '',
  premium: '',
  refundedPremium: '',
  descriptionHtml: '',
};

type Props = ModalProps & Pick<InsurancePolicy, 'effectiveDate' | 'expirationDate' | 'benefitTemplates' | 'descriptionHtml'> & {
  onSubmit: (values: InsurancePolicyUserFormValues) => void;
  policyUser: InsurancePolicyUserList | null;
}

const InsurancePolicyUserFormModal = ({ onSubmit, benefitTemplates, effectiveDate, expirationDate, descriptionHtml, policyUser, ...props }: Props) => {
  const { t } = useTranslation();
  const isEdit = !!policyUser;
  const initialValues = mergeInitialValues(defaultValues, { effectiveDate, expirationDate, descriptionHtml }, policyUser);

  const validationSchema = object({
    benefitTemplateId: number().required(t('validation:required')),
    insurerMemberId: string().optional(),
    name: string().required(t('validation:required')),
    dob: date().required(t('validation:required')),
    gender: string().required(t('validation:required')),
    occupation: string().optional(),
    email: string().email().optional(),
    effectiveDate: date().required(t('validation:required')),
    expirationDate: date().required(t('validation:required')),
    premium: number().required(t('validation:required')),
    refundedPremium: number().optional(),
    descriptionHtml: string().optional(),
  });

  return (
    <Modal {...props} size="xl">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <FormForm onHide={props.onHide} benefitTemplates={benefitTemplates} isEdit={isEdit} />
      </Formik>
    </Modal>
  )
};

export { InsurancePolicyUserFormModal };
